import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";

import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Item } from "../../../pojo/Item";
import { SeOrganizationTileComponent } from "../organization/se.organization.tile.component";
import { SeEventTileComponent } from "../organization/se.event.tile.component";
import { SeEvent } from "../event/se.event.details.view.component";
import { StudentEngagementOrg } from "../StudentEngagementOrg";
import SeOrgTile = StudentEngagementOrg.SeOrgTile;
import { AttendanceState } from "../attendance/attendance.service";

/**
 * Unified interface for items that can be displayed in tiles
 */
export interface TileItem extends S25ItemI.NumId {
    itemTypeId: Item.Ids;
    // Event-specific properties (optional)
    date?: string;
    locations?: string[];
    description?: string;
    occurrences?: { reservationId: number; attendanceState: AttendanceState }[];
    reservationId?: number;
    hostOrgId?: number;
    hostOrgName?: string;
    hostOrgLogoUrl?: string;
    imageUrl?: string;
    logoUrl?: string;
    /** When true, tile search already bulk-resolved logos; missing URL fields mean no logo. */
    logosResolvedFromSearch?: boolean;
    additionalOrganizations?: S25ItemI[];
    // Organization admin-specific properties (optional)
    type?: string;
    members?: S25ItemI[];
    primaryContact?: S25ItemI & { itemEmail?: string };
    rating?: S25ItemI & { organizationStanding?: "allow" | "deny" | "warn" };
    categories?: S25ItemI[];
    eventCount?: number;
    lastModified?: string;
    userRoles?: S25ItemI[];
}

/**
 * Stable {@code @for} track key for search grids. Occurrence tiles (reservation search) share
 * {@code itemId} across rows; {@code reservationId} is unique per tile.
 */
export function tileItemTrackKey(item: TileItem): string | number {
    if (item.reservationId != null) {
        return item.reservationId;
    }
    return `${item.itemTypeId}-${item.itemId}`;
}

/**
 * Item Tile Component
 * Displays either an organization tile or event tile based on the item type
 */
@TypeManagerDecorator("se-item-tile")
@Component({
    selector: "se-item-tile",
    template: `
        @if (item()) {
            @if (isEvent()) {
                <se-event-tile [event]="asEvent()!"></se-event-tile>
            } @else if (isOrganization()) {
                <se-organization-tile [org]="asOrganization()!"></se-organization-tile>
            }
        }
    `,
    styles: `
        :host {
            display: block;
            height: 100%;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SeOrganizationTileComponent, SeEventTileComponent],
})
export class ItemTileComponent {
    item = input.required<TileItem>();

    isEvent = computed(() => this.item().itemTypeId === Item.Ids.Event);
    isOrganization = computed(() => this.item().itemTypeId === Item.Ids.Organization);

    // Cast item as SeEvent for event tile
    asEvent = computed((): SeEvent | undefined => {
        const item = this.item();
        if (!this.isEvent()) {
            return undefined;
        }
        const hostOrg =
            item.hostOrgId && item.hostOrgName
                ? {
                      itemId: item.hostOrgId,
                      itemName: item.hostOrgName,
                      itemTypeId: Item.Ids.Organization,
                      logoUrl: item.hostOrgLogoUrl,
                      logosResolvedFromSearch: item.logosResolvedFromSearch,
                  }
                : undefined;
        return {
            itemId: item.itemId,
            itemName: item.itemName,
            itemTypeId: item.itemTypeId,
            date: item.date || "",
            locations: item.locations,
            description: item.description || "",
            occurrences: item.occurrences,
            imageUrl: item.imageUrl,
            logosResolvedFromSearch: item.logosResolvedFromSearch,
            hostOrg,
            reservationId: item.reservationId,
            additionalOrganizations: item.additionalOrganizations,
        };
    });

    asOrganization = computed((): SeOrgTile | undefined => {
        const item = this.item();
        if (!this.isOrganization()) {
            return undefined;
        }
        return {
            itemId: item.itemId,
            itemName: item.itemName,
            itemTypeId: item.itemTypeId,
            title: item.title,
            mission: item.mission,
            type: item.type,
            members: item.members ?? [],
            primaryContact: item.primaryContact,
            rating: item.rating,
            categories: item.categories ?? [],
            eventCount: item.eventCount,
            lastModified: item.lastModified,
            userRoles: item.userRoles,
            favorite: item.favorite,
            logoUrl: item.logoUrl,
            logosResolvedFromSearch: item.logosResolvedFromSearch,
        };
    });
}
